import { css, OmniElement } from 'omni-ui';
import { Om2Form } from 'omni-campaign-ui';
import CentralizedDataAPI from '../services/centralized-data-api.js';

/**
 * Campaign's customized Om2Form that handles "hydrating"/expanding async enums,
 * propagating change events in a specific way, and a little styling.
 *
 * @extends Om2Form
 */
export class CampaignDynamicForm extends Om2Form {
  /**
   * @property CentralizedDataAPI - API object used to "hydrate" async enums
   * @property token - Token needed by CentralizedDataAPI
   */
  static get properties() {
    return {
      CentralizedDataAPI: { type: Object },
      token: { type: String },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        /* TODO: Eventually remove and use omni-ui radio button styling */
        .omni label.radio:not(:last-child) {
          margin-right: 1.5em;
        }
        .omni label.radio {
          white-space: nowrap;
        }
      `,
    ];
  }

  updated(changedProperties) {
    super.updated(changedProperties);

    if (
      changedProperties.has('env') ||
      (changedProperties.has('token') && this.token)
    ) {
      this.CentralizedDataAPI = new CentralizedDataAPI(this.token, this.env);
    }

    if (changedProperties.has('schema') && !this.schema.hydrated) {
      Object.entries(this.schema).map(async ([key, schemaItem]) => {
        if (Object.keys(schemaItem).includes('asyncEnum')) {
          await this._hydrateEnum(key);
        }
      });
    }
  }

  /**
   * Pulls enum options from backend and updates (hydrates) the enum schema definition
   *
   * @async
   * @param {string} key - The schema key of the enum that needs "hydrating"
   */
  async _hydrateEnum(key) {
    const schemaItem = this.schema[key];
    try {
      const asyncEnum = schemaItem?.asyncEnum;
      const apiId = asyncEnum?.uuid_api_connection;
      const queryVars = new URLSearchParams(asyncEnum?.params);

      // If id is not defined in the schema, nothing to do (ignore).
      if (apiId === undefined) return;

      if (this.context?.clientId) {
        queryVars.set('guid_client', this.context.clientId);
      }

      let options = await this.CentralizedDataAPI.request(apiId, queryVars);
      options = options.data.map(opt => ({
        name: opt.name,
        id: opt.uuid_object,
      }));

      const eventDetail = {
        detail: {
          key,
          options,
          moduleIndex: this.context?.moduleIndex,
        },
      };

      this.dispatchNewEvent('asyncenumhydration', eventDetail);
    } catch (error) {
      console.error(error); // eslint-disable-line no-console
    }
  }

  /**
   * Overrides parent's _dispatchChange() function. This gets called when there are
   * changes to the form. For Campaign we need to send a "omni-module-changed" event.
   *
   */
  _dispatchChange() {
    const moduleId = this.context?.uuid_module;
    this.dispatchNewEvent('omni-module-changed', {
      detail: {
        moduleId,
        saveAction: this.submit.bind(this),
        validateAction: this.validate.bind(this),
        moduleType: 'dynamic form',
      },
    });
  }

  /**
   * This gets called by lit in a similar manner to update(), but earlier and
   * any property changes done within the callback don't trigger another update. We can
   * handle saving and loading of "shared" input field in our form by intercepting when
   * this.data is set (load), and when this.save is set (save).
   *
   * @param {Object} changedProperties - Dictionary of changed properties
   */
  willUpdate(changedProperties) {
    // Save "shared" data by intercepting the save callback
    if (changedProperties.has('save') && this.save) {
      this.save = this._wrapSave(this.save);
    }

    // Load saved "shared" data by pulling it from the context
    if (
      (changedProperties.has('context') ||
        changedProperties.has('data') ||
        changedProperties.has('schema')) &&
      this.context &&
      this.schema
    ) {
      const sharedData = {};
      // Check if we have any "shared" input fields in this form, and if so pull any
      // saved data for them from our context.
      Object.entries(this.schema).forEach(([id, fieldSchema]) => {
        if (fieldSchema.sharedField) {
          const fieldValue = this.context.sharedData[`/form/${id}`]?.value;
          if (fieldValue !== undefined) {
            sharedData[id] = fieldValue;
          }
        }
      });
      if (Object.keys(sharedData).length) {
        // If there's any shared data, merge it into this.data (otherwise do nothing)
        this.data = { ...(this.data ?? {}), ...sharedData };
      }
    }
  }

  /**
   * Wrap our save function with a light layer which splits the data being saved into
   * two: the first is the standard one for normal input fields, the second is for
   * "shared" input fields that need to be saved separately.
   *
   * @param {Function} saveFunc - The original save callback function
   * @returns {Function} The wrapped function which splits our data and then calls save
   */
  _wrapSave(saveFunc) {
    // Just in case, check that we're not wrapping the same function more than once
    if (saveFunc.wrapped) return saveFunc;

    const newFunc = saveData => {
      const sharedData = {};
      const normalData = {};
      Object.entries(saveData).forEach(([key, value]) => {
        if (this.schema?.[key]?.sharedField) {
          // Special "Shared" input field
          // Prefix the key to avoid collisions with other modules
          sharedData[`/form/${key}`] = value;
        } else {
          // Normal input field
          normalData[key] = value;
        }
      });
      saveFunc(normalData, sharedData);
    };

    newFunc.wrapped = true;
    return newFunc;
  }
}

OmniElement.register('campaign-dynamic-form', CampaignDynamicForm);
